/**
 * Compute the provenance (source) of each task label.
 * Shows users which labels came from email LLM, attachment LLM, or pipeline injection.
 */

export interface LabelSource {
  label: string;
  source: 'email' | 'attachment' | 'pipeline';
  detail: string;
  confidence?: number;
}

/**
 * Determine where each task label came from by comparing against email/attachment classifications.
 *
 * Priority: attachment match → email match → known pipeline labels → unknown pipeline.
 */
export function computeLabelProvenance(
  taskLabels: string[],
  emailClassification: string | null | undefined,
  attachments: Array<{
    fileName: string;
    labels: string[];
    classificationConfidence: number | null;
  }>
): LabelSource[] {
  const sources: LabelSource[] = [];

  // Build attachment lookup: each label a file carries → [{fileName, confidence}]
  // With multi-label attachments, one attachment can contribute to multiple labels.
  const attachmentsByClassification = new Map<
    string,
    Array<{ fileName: string; confidence: number | null }>
  >();
  for (const att of attachments) {
    for (const label of att.labels) {
      const list = attachmentsByClassification.get(label) ?? [];
      list.push({ fileName: att.fileName, confidence: att.classificationConfidence });
      attachmentsByClassification.set(label, list);
    }
  }

  // Track which classification labels have been attributed (to avoid double-attributing)
  const usedClassifications = new Set<string>();

  for (const label of taskLabels) {
    // 1. Known pipeline-injected labels (deterministic, not from LLM)
    //    Keep in sync with: apps/api/src/core/email-processing/types.ts (PROCEDURAL_LABELS)
    if (label === 'new_patient') {
      sources.push({ label, source: 'pipeline', detail: 'Patient match (new)' });
      continue;
    }
    if (label === 'existing_patient') {
      sources.push({ label, source: 'pipeline', detail: 'Patient match (existing)' });
      continue;
    }
    if (label === 'urgent' || label === 'stat') {
      sources.push({ label, source: 'pipeline', detail: 'Extraction urgency' });
      continue;
    }
    if (label === 'voicemail') {
      sources.push({ label, source: 'pipeline', detail: 'Audio detection' });
      continue;
    }

    // 2. Check attachment classifications
    const attMatches = attachmentsByClassification.get(label);
    if (attMatches && attMatches.length > 0 && !usedClassifications.has(label)) {
      usedClassifications.add(label);
      // Attribute to the first matching attachment
      const att = attMatches[0]!;
      sources.push({
        label,
        source: 'attachment',
        detail: att.fileName,
        confidence: att.confidence ?? undefined,
      });
      continue;
    }

    // 3. Check email classification
    if (emailClassification && label === emailClassification) {
      sources.push({ label, source: 'email', detail: 'Email subject/body' });
      continue;
    }

    // 4. Fallback — likely from email or an unmatched source
    sources.push({ label, source: 'email', detail: 'Classification' });
  }

  return sources;
}
